import { useState, useContext } from 'react';
import { CartContext } from '../context/CartContext';
import { toast } from './Toast';

const formatKES = (n) =>
    'KES ' + Number(n || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 });

/**
 * "Apply Promo Code" input for the Cart and Checkout order summaries.
 * Reads/writes the applied coupon through CartContext; totals are recalculated
 * server-side and flow back via the cart summary.
 *
 * @param {{ disabled?: boolean, disabledHint?: string, onApplied?: () => void }} props
 */
export default function CouponInput({ disabled = false, disabledHint = '', onApplied } = {}) {
    const { cart, applyCoupon, removeCoupon } = useContext(CartContext);
    const [code, setCode] = useState('');
    const [loading, setLoading] = useState(false);

    const appliedCode = cart?.couponCode || null;
    const couponDiscount = cart?.summary?.couponDiscount || 0;
    const isApplied = !!appliedCode && couponDiscount > 0;

    const handleApply = async (e) => {
        e.preventDefault();
        if (disabled) {
            toast.error(disabledHint || 'Coupon cannot be applied right now');
            return;
        }
        const trimmed = code.trim();
        if (!trimmed) {
            toast.error('Please enter a coupon code');
            return;
        }
        setLoading(true);
        try {
            const res = await applyCoupon(trimmed);
            toast.success(res?.data?.message || 'Coupon applied successfully!');
            setCode('');
            onApplied?.();
        } catch (err) {
            toast.error(err?.response?.data?.message || 'Could not apply this coupon');
        } finally {
            setLoading(false);
        }
    };

    const handleRemove = async () => {
        setLoading(true);
        try {
            await removeCoupon();
            toast.success('Coupon removed');
        } catch {
            toast.error('Could not remove coupon');
        } finally {
            setLoading(false);
        }
    };

    if (isApplied) {
        return (
            <div className="flex items-center justify-between rounded-lg border border-green-200 bg-green-50 px-3 py-2">
                <div className="flex items-center gap-2 min-w-0">
                    <i className="ri-coupon-3-line text-green-600"></i>
                    <span className="text-sm font-medium text-green-700 truncate">
                        {appliedCode}
                    </span>
                    <span className="text-xs text-green-600">
                        (-{formatKES(couponDiscount)})
                    </span>
                </div>
                <button
                    type="button"
                    onClick={handleRemove}
                    disabled={loading}
                    className="text-xs text-muted-foreground hover:text-red-600 transition-colors disabled:opacity-50"
                    aria-label="Remove coupon"
                >
                    <i className="ri-close-line text-base"></i>
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-1">
            <form onSubmit={handleApply} className="flex gap-2">
                <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    placeholder="Coupon code"
                    disabled={loading || disabled}
                    className="flex-1 min-w-0 px-3 py-2 text-sm border border-border rounded-lg bg-background text-foreground uppercase placeholder:normal-case placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                />
                <button
                    type="submit"
                    disabled={loading || disabled}
                    className="px-4 py-2 text-sm font-semibold rounded-lg bg-primary text-white hover:brightness-110 transition-all disabled:opacity-50"
                >
                    {loading ? '...' : 'Apply'}
                </button>
            </form>
            {disabled && disabledHint ? (
                <p className="text-[11px] text-amber-600">{disabledHint}</p>
            ) : null}
        </div>
    );
}
